import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import type { BrandLearning } from "@/types/brand-context";
import { DEFAULT_STALE_TIME } from "@/constants";

type BrandLearningSnapshot = {
  queryKey: readonly unknown[];
  data: BrandLearning[] | undefined;
}[];

export const brandLearningsQueryKey = (brandId: number | null | undefined) =>
  ["brand", "brand-learnings", brandId] as const;

const takeSnapshot = (brandId: number): BrandLearningSnapshot => {
  const queryKey = brandLearningsQueryKey(brandId);
  const data = queryClient.getQueryData<BrandLearning[]>(queryKey);
  return [{ queryKey, data }];
};

const restoreSnapshot = (snapshot: BrandLearningSnapshot) => {
  for (const { queryKey, data } of snapshot) {
    queryClient.setQueryData(queryKey, data);
  }
};

let optimisticIdCounter = -1;

export const useGetBrandLearnings = (brandId: number | null) =>
  useQuery<BrandLearning[]>({
    queryKey: brandLearningsQueryKey(brandId),
    queryFn: () => apiService.get<BrandLearning[]>(`/brands/${brandId}/brand-context/learnings`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export type BrandLearningInput = {
  title: string;
  body: string;
  category: string;
  tags?: string[];
};

export const useCreateBrandLearning = () =>
  useMutation<
    BrandLearning,
    unknown,
    { brandId: number } & BrandLearningInput,
    { snapshot: BrandLearningSnapshot }
  >({
    mutationFn: ({ brandId, ...data }) =>
      apiService.post<BrandLearning, BrandLearningInput>(`/brands/${brandId}/brand-context/learnings`, data),
    onMutate: async (variables) => {
      const queryKey = brandLearningsQueryKey(variables.brandId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brandId);

      const optimistic: BrandLearning = {
        id: String(optimisticIdCounter--),
        title: variables.title,
        body: variables.body,
        category: variables.category,
        tags: variables.tags ?? [],
        authorName: "",
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueryData<BrandLearning[]>(queryKey, (prev) =>
        prev ? [optimistic, ...prev] : [optimistic],
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(error);
    },
    onSuccess: (serverData, variables) => {
      const queryKey = brandLearningsQueryKey(variables.brandId);
      queryClient.setQueryData<BrandLearning[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((l) => (Number(l.id) < 0 && l.title === variables.title ? serverData : l));
      });
    },
  });

export const useUpdateBrandLearning = () =>
  useMutation<
    BrandLearning,
    unknown,
    { brandId: number; learningId: string } & BrandLearningInput,
    { snapshot: BrandLearningSnapshot }
  >({
    mutationFn: ({ brandId, learningId, ...data }) =>
      apiService.patch<BrandLearning, BrandLearningInput>(
        `/brands/${brandId}/brand-context/learnings/${learningId}`,
        data,
      ),
    onMutate: async (variables) => {
      const queryKey = brandLearningsQueryKey(variables.brandId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brandId);

      queryClient.setQueryData<BrandLearning[]>(queryKey, (prev) =>
        prev
          ? prev.map((l) =>
              l.id === variables.learningId
                ? {
                    ...l,
                    title: variables.title,
                    body: variables.body,
                    category: variables.category,
                    tags: variables.tags ?? [],
                  }
                : l,
            )
          : prev,
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(error);
    },
    onSuccess: (serverData, variables) => {
      const queryKey = brandLearningsQueryKey(variables.brandId);
      queryClient.setQueryData<BrandLearning[]>(queryKey, (prev) =>
        prev ? prev.map((l) => (l.id === variables.learningId ? serverData : l)) : prev,
      );
    },
  });

export const useDeleteBrandLearning = () =>
  useMutation<unknown, unknown, { brandId: number; learningId: string }, { snapshot: BrandLearningSnapshot }>({
    mutationFn: ({ brandId, learningId }) =>
      apiService.delete(`/brands/${brandId}/brand-context/learnings/${learningId}`),
    onMutate: async (variables) => {
      const queryKey = brandLearningsQueryKey(variables.brandId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brandId);

      queryClient.setQueryData<BrandLearning[]>(queryKey, (prev) =>
        prev ? prev.filter((l) => l.id !== variables.learningId) : prev,
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiError(error);
    },
  });
